// The server refuses a student's message outside a thread's window (before the
// session opens, or after the thread's due date) or once the thread's goal is
// reached, with a plain-text error that carries one of three fixed phrases; see
// sendKaiCoachMessage in
// backend/app/kai-coach-api.go. The API client keeps a plain-text error body as
// the error's message, so the phrase arrives intact and the UI can say what
// happened instead of falling back to a generic failure.
//
// This lives apart from the service so tests that mock the service keep it.

export type CoachThreadClosedReason = 'closed' | 'complete' | 'notOpen';

const CLOSED_PHRASE = 'coach thread closed';
const COMPLETE_PHRASE = 'coach thread complete';
const NOT_OPEN_PHRASE = 'coach thread not open';

export class CoachThreadClosedError extends Error {
  readonly reason: CoachThreadClosedReason;

  constructor(reason: CoachThreadClosedReason) {
    super(
      reason === 'closed'
        ? 'The coach thread has closed.'
        : reason === 'complete'
          ? 'The coach thread is complete: its goal was reached.'
          : 'The coach thread has not opened yet.',
    );
    this.name = 'CoachThreadClosedError';
    this.reason = reason;
  }
}

/** The window refusal an error carries, or null for any other failure. */
export function coachThreadClosedReason(error: unknown): CoachThreadClosedReason | null {
  if (error instanceof CoachThreadClosedError) {
    return error.reason;
  }
  const message = error instanceof Error ? error.message : '';
  if (message.includes(CLOSED_PHRASE)) {
    return 'closed';
  }
  if (message.includes(COMPLETE_PHRASE)) {
    return 'complete';
  }
  if (message.includes(NOT_OPEN_PHRASE)) {
    return 'notOpen';
  }
  return null;
}
